/**
 * Pan and zoom controller for diagram navigation
 * Handles panzoom initialization, events, and animations
 */
import { resolveDependencies, createEventManager } from './dependency-resolver.js';

/**
 * Creates a pan/zoom controller with standard dependencies
 * @param {Object} options - Configuration options
 * @returns {Object} - Pan/zoom controller module
 */
export function createPanZoomController(options = {}) {
  const { asyncUtils, eventBus } = resolveDependencies(options);
  const events = createEventManager(eventBus);
  
  /**
   * Initialize Panzoom on the SVG
   * @param {SVGElement} svg - The SVG element to apply panzoom to
   * @param {HTMLElement} wrapper - The wrapper element containing the SVG
   * @returns {Object|null} The panzoom instance or null if initialization failed
   */
  function initializePanzoom(svg, wrapper) {
    // Ensure the external library is available
    if (typeof Panzoom !== 'function') {
      console.error('Panzoom library not available');
      events.emit('panzoom:error', { error: new Error('Panzoom library not available') });
      return null;
    }
    
    try {
      // Ensure SVG has no conflicting positioning styles or visible state
      svg.style.position = '';
      svg.style.left = '';
      svg.style.top = '';
      svg.style.transform = '';
      svg.classList.remove('svg-ready'); // Make sure it's hidden initially
      
      // Force a browser reflow to ensure measurements are accurate
      wrapper.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      svg.getBoundingClientRect();
      const svgRect = svg.getBoundingClientRect();
      
      // Calculate the offsets needed to center the SVG
      let centerX = 0;
      let centerY = 0;
      
      if (svgRect.width < wrapperRect.width) {
        centerX = (wrapperRect.width - svgRect.width) / 2;
      }
      
      if (svgRect.height < wrapperRect.height) {
        centerY = (wrapperRect.height - svgRect.height) / 2;
      }
      
      // Create panzoom instance
      const panzoomInstance = Panzoom(svg, {
        maxScale: 8,
        minScale: 1,
        startScale: 1,
        startX: centerX,
        startY: centerY,
        step: 0.25,
        animate: false,  // Disable animations
        disablePan: false,
        disableZoom: false,
        excludeClass: 'diagram-controls',
        // Enable canvas mode for better panning (entire container area is draggable)
        canvas: true
      });
      
      // Store instance on the wrapper element
      wrapper._panzoom = panzoomInstance;
      
      // Animation cancellation tracking
      let currentAnimationCancel = null;
      
      // Set up global access for animation cancellation
      wrapper.cancelAnimation = function() {
        if (currentAnimationCancel) {
          currentAnimationCancel();
          currentAnimationCancel = null;
        }
      };
      
      // Store animation cancel reference for later use
      wrapper.setAnimationCancel = function(cancelFn) {
        currentAnimationCancel = cancelFn;
      };
      
      // Set up global access for button handlers (for backward compatibility)
      window.panzoomStore = {
        instance: panzoomInstance,
        wrapper: wrapper,
        zoomIn: zoomInFn.bind(null, panzoomInstance, wrapper),
        zoomOut: zoomOutFn.bind(null, panzoomInstance, wrapper),
        reset: resetFn.bind(null, panzoomInstance, wrapper)
      };
      
      // Set up event listeners for panzoom events
      setupPanzoomEventHandlers(svg, wrapper, panzoomInstance);
      
      // Set up button handlers
      setupButtonHandlers(panzoomInstance, wrapper);
      
      // Emit initialized event
      events.emit('panzoom:initialized', { 
        instance: panzoomInstance,
        wrapper: wrapper
      });
      
      return panzoomInstance;
    } catch (err) {
      console.error('Error initializing panzoom:', err);
      events.emit('panzoom:error', { error: err });
      return null;
    }
  }
  
  /**
   * Function for zooming in
   * @param {Object} panzoomInstance - The panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element
   * @returns {boolean} Success indicator
   */
  function zoomInFn(panzoomInstance, wrapper) {
    if (panzoomInstance && wrapper) {
      // Cancel any ongoing animation
      if (typeof wrapper.cancelAnimation === 'function') {
        wrapper.cancelAnimation();
      }
      
      // Get the center of the wrapper
      const wrapperRect = wrapper.getBoundingClientRect();
      const centerX = wrapperRect.left + wrapperRect.width / 2;
      const centerY = wrapperRect.top + wrapperRect.height / 2;
      
      // Zoom to the center point without animation
      panzoomInstance.zoomToPoint(
        panzoomInstance.getScale() * (1 + panzoomInstance.getOptions().step),
        { clientX: centerX, clientY: centerY },
        { animate: false }
      );
      return true;
    }
    return false;
  }
  
  /**
   * Function for zooming out
   * @param {Object} panzoomInstance - The panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element
   * @returns {boolean} Success indicator
   */
  function zoomOutFn(panzoomInstance, wrapper) {
    if (panzoomInstance && wrapper) {
      // Cancel any ongoing animation
      if (typeof wrapper.cancelAnimation === 'function') {
        wrapper.cancelAnimation();
      }
      
      // Get the center of the wrapper
      const wrapperRect = wrapper.getBoundingClientRect();
      const centerX = wrapperRect.left + wrapperRect.width / 2;
      const centerY = wrapperRect.top + wrapperRect.height / 2;
      
      // Zoom out from the center point without animation
      panzoomInstance.zoomToPoint(
        panzoomInstance.getScale() / (1 + panzoomInstance.getOptions().step),
        { clientX: centerX, clientY: centerY },
        { animate: false }
      );
      return true;
    }
    return false;
  }
  
  /**
   * Function for resetting zoom
   * @param {Object} panzoomInstance - The panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element
   * @returns {boolean} Success indicator
   */
  function resetFn(panzoomInstance, wrapper) {
    if (panzoomInstance) {
      // Cancel any ongoing animation
      if (wrapper && typeof wrapper.cancelAnimation === 'function') {
        wrapper.cancelAnimation();
      }
      
      panzoomInstance.reset({ animate: false });
      return true;
    }
    return false;
  }
  
  /**
   * Centers a node in the viewport by adjusting the pan position
   * @param {Object} panzoomInstance - The panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element
   * @param {number} nodeX - The X coordinate of the node to center
   * @param {number} nodeY - The Y coordinate of the node to center
   */
  function centerNodeInViewport(panzoomInstance, wrapper, nodeX, nodeY) {
    // Get the current scale
    const currentScale = panzoomInstance.getScale();
    
    // Get the viewport dimensions and center
    const viewportRect = wrapper.getBoundingClientRect();
    const viewportCenterX = viewportRect.left + viewportRect.width / 2;
    const viewportCenterY = viewportRect.top + viewportRect.height / 2;
    
    // Get current pan values
    const currentPan = panzoomInstance.getPan();
    
    // Calculate delta in screen coordinates
    const screenDeltaX = viewportCenterX - nodeX;
    const screenDeltaY = viewportCenterY - nodeY;
    
    // Adjust for current scale - we need to apply the delta in the scaled coordinate system
    const panDeltaX = screenDeltaX / currentScale;
    const panDeltaY = screenDeltaY / currentScale;
    
    // Apply the pan to center the node in the viewport
    const newPanX = currentPan.x + panDeltaX;
    const newPanY = currentPan.y + panDeltaY;
    
    panzoomInstance.pan(newPanX, newPanY);
    
    return { newPanX, newPanY };
  }
  
  /**
   * Performs an animated zoom to a target scale while centering a specific node
   * @param {Object} panzoomInstance - The panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element
   * @param {HTMLElement} node - The node element to center and zoom to
   * @param {number} targetZoom - The target zoom level
   * @param {Object} animOptions - Optional settings
   * @returns {Function} - Function to cancel the animation
   */
  function animatedZoomToNode(panzoomInstance, wrapper, node, targetZoom, animOptions = {}) {
    if (!node) {
      console.warn('No node element provided for animation');
      return () => {};
    }
    
    // Merge options with defaults
    const options = {
      duration: animOptions.duration || 1000,
      ...animOptions
    };
    
    // Animation ID for cancellation
    let animationId = null;
    
    // Flag to track if animation was cancelled
    let isCancelled = false;
    
    // Get current scale and pan values
    const startScale = panzoomInstance.getScale();
    const startPan = panzoomInstance.getPan();
    
    // Get the viewport center
    const viewportRect = wrapper.getBoundingClientRect();
    const viewportCenterX = viewportRect.left + viewportRect.width / 2;
    const viewportCenterY = viewportRect.top + viewportRect.height / 2;
    
    // Get initial node position to calculate the total distance to move
    const initialNodeRect = node.getBoundingClientRect();
    const initialNodeX = initialNodeRect.left + initialNodeRect.width / 2;
    const initialNodeY = initialNodeRect.top + initialNodeRect.height / 2;
    
    // Calculate the total offset between node and viewport center
    const totalOffsetX = initialNodeX - viewportCenterX;
    const totalOffsetY = initialNodeY - viewportCenterY;
    
    // Calculate adjustment needed in pan coordinates (accounting for scale)
    const totalPanAdjustX = totalOffsetX / startScale;
    const totalPanAdjustY = totalOffsetY / startScale;
    
    // Keep track of the last applied easing value
    let lastPanEased = 0;
    
    // Function to cancel ongoing animation
    function cancelAnimation() {
      if (animationId) {
        isCancelled = true;
        cancelAnimationFrame(animationId);
        animationId = null;
        
        // Remove event listeners
        wrapper.removeEventListener('mousedown', cancelAnimation);
        wrapper.removeEventListener('wheel', cancelAnimation);
        wrapper.removeEventListener('touchstart', cancelAnimation);
      }
    }
    
    // Create an AbortController for the animation if supported
    let controller;
    try {
      controller = new AbortController();
      const signal = controller.signal;
      
      // Add event listeners to cancel animation on user interaction with abort signal
      wrapper.addEventListener('mousedown', cancelAnimation, { signal });
      wrapper.addEventListener('wheel', cancelAnimation, { signal });
      wrapper.addEventListener('touchstart', cancelAnimation, { signal });
    } catch (e) {
      // Fallback for browsers without AbortController support
      wrapper.addEventListener('mousedown', cancelAnimation);
      wrapper.addEventListener('wheel', cancelAnimation);
      wrapper.addEventListener('touchstart', cancelAnimation);
    }
    
    // Check if Web Animation API and animatePromise are available for smoother animation
    const canUseModernAnimation = 
      asyncUtils && 
      typeof asyncUtils.animatePromise === 'function' && 
      typeof Element.prototype.animate === 'function';
    
    if (canUseModernAnimation) {
      try {
        // Improved modern approach using Web Animation API with pre-calculated movements
        const animationController = document.createElement('div');
        animationController.style.display = 'none';
        wrapper.appendChild(animationController);
        
        // Store the last applied easing values for incremental calculations
        let lastPanEased = 0;
        let lastZoomEased = 0;
        
        // Create animation options
        const animationOptions = {
          duration: options.duration,
          easing: 'cubic-bezier(0.42, 0, 0.58, 1)', // ease-in-out
          fill: 'forwards'
        };

        // Function to update panzoom values each frame
        function updateAnimation(timestamp) {
          if (isCancelled) return;

          // Calculate progress based on elapsed time
          const elapsed = timestamp - animationStartTime;
          const progress = Math.min(elapsed / options.duration, 1);
          
          // Apply cubic easing for panning (faster at beginning and end, slower in middle)
          const panEased = progress < 0.5 ? 
            4 * progress * progress * progress : 
            1 - Math.pow(-2 * progress + 2, 3) / 2;
          
          // Apply quartic easing for zooming
          const zoomEased = progress * progress * progress * progress;
          
          // Calculate the easing increments since the last frame
          const panEasingIncrement = panEased - lastPanEased;
          const zoomEasingIncrement = zoomEased - lastZoomEased;
          
          // Update the last easing values for next frame
          lastPanEased = panEased;
          lastZoomEased = zoomEased;
          
          // Get current pan and scale
          const currentPan = panzoomInstance.getPan();
          const currentScale = panzoomInstance.getScale();
          
          // Calculate incremental pan adjustment based on total distance and pan easing increment
          const incrementalPanX = totalPanAdjustX * panEasingIncrement;
          const incrementalPanY = totalPanAdjustY * panEasingIncrement;
          
          // Apply pan adjustment first (negative because we want to move in opposite direction)
          panzoomInstance.pan(
            currentPan.x - incrementalPanX, 
            currentPan.y - incrementalPanY,
            { animate: false }
          );
          
          // Calculate current scale using zoom easing increment
          const scaleIncrement = (targetZoom - startScale) * zoomEasingIncrement;
          const newScale = currentScale + scaleIncrement;
          
          // Use zoomToPoint with the viewport center to avoid unwanted panning from zoom
          panzoomInstance.zoomToPoint(
            newScale,
            { clientX: viewportCenterX, clientY: viewportCenterY },
            { animate: false }
          );
          
          // Continue animation if not complete
          if (progress < 1 && !isCancelled) {
            animationId = requestAnimationFrame(updateAnimation);
          } else if (progress >= 1) {
            // Animation complete
            animationId = null;
            
            // Force one final centering calculation at the end
            finalizeNodePosition();
            
            // Remove the animation controller element
            if (animationController.parentNode) {
              animationController.parentNode.removeChild(animationController);
            }
            
            // Emit animation complete event
            events.emit('panzoom:animationComplete', {
              success: true,
              nodeId: node.id
            });
            
            // Remove event listeners
            wrapper.removeEventListener('mousedown', cancelAnimation);
            wrapper.removeEventListener('wheel', cancelAnimation);
            wrapper.removeEventListener('touchstart', cancelAnimation);
          }
        }
        
        // Track animation start time
        const animationStartTime = performance.now();
        
        // Start the animation
        animationId = requestAnimationFrame(updateAnimation);
        
        // Create a cleanup function
        const cleanup = () => {
          cancelAnimation();
          if (controller) {
            try {
              controller.abort();
            } catch (e) {
              // Ignore errors from aborting
            }
          }
          
          // Remove the animation controller
          if (animationController.parentNode) {
            animationController.parentNode.removeChild(animationController);
          }
        };
        
        return cleanup;
      } catch (e) {
        console.warn('Modern animation approach failed, falling back to traditional animation', e);
        // Fall back to traditional approach if modern approach fails
      }
    }
    
    // Traditional animation approach using requestAnimationFrame
    const startTime = performance.now();
    
    // Animation function that applies incremental movements
    function animate(timestamp) {
      // If cancelled, stop immediately
      if (isCancelled) {
        return;
      }
      
      // Calculate progress
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / options.duration, 1);
      
      // Apply cubic easing for panning (faster at beginning and end, slower in middle)
      const panEased = progress < 0.5 ? 
        4 * progress * progress * progress : 
        1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      const zoomEased = progress * progress * progress * progress;
      
      // Calculate the pan easing increment since last frame
      const panEasingIncrement = panEased - lastPanEased;
      lastPanEased = panEased;
      
      // Get current pan values
      const currentPan = panzoomInstance.getPan();
      
      // Calculate incremental pan adjustment based on total distance and pan easing increment
      const incrementalPanX = totalPanAdjustX * panEasingIncrement;
      const incrementalPanY = totalPanAdjustY * panEasingIncrement;
      
      // Apply pan adjustment (negative because we want to move in opposite direction)
      panzoomInstance.pan(
        currentPan.x - incrementalPanX, 
        currentPan.y - incrementalPanY,
        { animate: false }
      );

      // Calculate current scale using zoom easing - interpolate from start to target
      const newScale = startScale + ((targetZoom - startScale) * zoomEased);
      
      // Use zoomToPoint with the viewport center to avoid unwanted panning from zoom
      panzoomInstance.zoomToPoint(
        newScale,
        { clientX: viewportCenterX, clientY: viewportCenterY },
        { animate: false }
      );
      
      // Continue animation if not complete
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        // Animation complete
        animationId = null;
        
        // Remove event listeners
        wrapper.removeEventListener('mousedown', cancelAnimation);
        wrapper.removeEventListener('wheel', cancelAnimation);
        wrapper.removeEventListener('touchstart', cancelAnimation);
        
        // Force one final centering calculation at the end
        finalizeNodePosition();
        
        // Emit animation complete event
        events.emit('panzoom:animationComplete', {
          success: true,
          nodeId: node.id
        });
      }
    }
    
    // Helper function to ensure node is centered at the end of animation
    function finalizeNodePosition() {
      const finalNodeRect = node.getBoundingClientRect();
      const finalNodeX = finalNodeRect.left + finalNodeRect.width / 2;
      const finalNodeY = finalNodeRect.top + finalNodeRect.height / 2;
      
      const finalOffsetX = finalNodeX - viewportCenterX;
      const finalOffsetY = finalNodeY - viewportCenterY;
      
      if (Math.abs(finalOffsetX) > 2 || Math.abs(finalOffsetY) > 2) {
        const finalPan = panzoomInstance.getPan();
        const finalScale = panzoomInstance.getScale();
        
        panzoomInstance.pan(
          finalPan.x - finalOffsetX / finalScale, 
          finalPan.y - finalOffsetY / finalScale
        );
      }
    }
    
    // Start the animation
    animationId = requestAnimationFrame(animate);
    
    // Return a function that can be used to cancel the animation from the outside
    return cancelAnimation;
  }
  
  /**
   * Set up event listeners for panzoom events
   * @param {SVGElement} svg - The SVG element with panzoom
   * @param {HTMLElement} wrapper - The wrapper element
   * @param {Object} panzoomInstance - The panzoom instance
   */
  function setupPanzoomEventHandlers(svg, wrapper, panzoomInstance) {
    // Make sure wrapper has overflow hidden
    wrapper.style.overflow = 'hidden';
    
    // Get zoom control buttons
    const zoomInButton = document.getElementById('zoomIn');
    const zoomOutButton = document.getElementById('zoomOut');
    const zoomResetButton = document.getElementById('zoomReset');
    
    // Update cursor based on zoom level
    function updateCursor() {
      const scale = panzoomInstance.getScale();
      wrapper.style.cursor = scale > 1 ? 'grab' : 'default';
      
      // When actively panning, show the grabbing cursor
      if (wrapper._panning) {
        wrapper.style.cursor = 'grabbing';
      }
    }
    
    // Update button states based on current panzoom state
    function updateButtonStates() {
      const scale = panzoomInstance.getScale();
      const pan = panzoomInstance.getPan();
      
      if (zoomInButton) zoomInButton.disabled = scale >= 8;
      if (zoomOutButton) zoomOutButton.disabled = scale <= 1;
      if (zoomResetButton) zoomResetButton.disabled = scale === 1 && pan.x === 0 && pan.y === 0;
    }
    
    // Enable shift+wheel for focal-point zooming
    wrapper.addEventListener('wheel', function(event) {
      event.preventDefault();
      
      // Cancel any ongoing animation
      wrapper.cancelAnimation();
      
      // Only zoom when shift key is pressed
      if (event.shiftKey) {
        panzoomInstance.zoomWithWheel(event);
      }
    }, { passive: false });
    
    // Function to handle node zoom (shared between double-click and double-tap)
    function handleNodeZoom(node) {
      if (!node) return;
      
      // Cancel any ongoing animation
      wrapper.cancelAnimation();
        
      // Calculate target zoom level (80% of max zoom)
      const options = panzoomInstance.getOptions();
      const maxScale = options.maxScale || 8;
      const targetZoom = maxScale * 0.8;
        
      // Animated zoom and pan to center the node (passing the actual node element)
      // Use default duration by not specifying options
      const cancelFn = animatedZoomToNode(panzoomInstance, wrapper, node, targetZoom);
      wrapper.setAnimationCancel(cancelFn);
      
      // Emit zoom event
      events.emit('panzoom:nodeZoom', {
        nodeId: node.id,
        targetZoom: targetZoom
      });
    }
    
    // Track touch events for double-tap detection
    let lastTapTime = 0;
    let lastTapNode = null;
    const doubleTapDelay = 300; // ms
    
    // Add touch handler for double-tap detection
    wrapper.addEventListener('touchend', function(event) {
      // Prevent default behavior to avoid zoom/scroll issues on mobile
      if (event.cancelable) event.preventDefault();
      
      const now = Date.now();
      
      // Find if tap was on a node
      const touch = event.changedTouches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const node = element ? element.closest('rect, circle, polygon, .node') : null;
      
      if (node) {
        // Check if this is a double-tap on the same node
        if (lastTapNode === node && (now - lastTapTime) < doubleTapDelay) {
          // This is a double-tap, handle the zoom
          handleNodeZoom(node);
          
          // Reset tracking to prevent triple-tap issues
          lastTapTime = 0;
          lastTapNode = null;
        } else {
          // This is a first tap, store for potential double-tap
          lastTapTime = now;
          lastTapNode = node;
        }
      } else {
        // Tap wasn't on a node, reset double-tap tracking
        lastTapTime = 0;
        lastTapNode = null;
      }
    }, { passive: false });
    
    // Add double-click to center and zoom to a node in the viewport
    wrapper.addEventListener('dblclick', function(event) {
      // Check if the click was on a node (rect or other mermaid node shapes)
      const node = event.target.closest('rect, circle, polygon, .node');
      
      // Only handle double-click if it's on a node
      if (node) {
        handleNodeZoom(node);
      }
      // Ignore double-clicks elsewhere (no else clause)
    });
    
    // Set up event listeners for panzoom events
    svg.addEventListener('panzoomchange', function(event) {
      updateCursor();
      updateButtonStates();
      
      // Emit our own event
      events.emit('panzoom:change', {
        scale: panzoomInstance.getScale(),
        pan: panzoomInstance.getPan()
      });
    });
    
    // Track panning state for cursor changes
    svg.addEventListener('panzoomstart', function(event) {
      // Cancel any ongoing animation
      wrapper.cancelAnimation();
      
      wrapper._panning = true;
      updateCursor();
      
      // Emit our own event
      events.emit('panzoom:start', {
        scale: panzoomInstance.getScale(),
        pan: panzoomInstance.getPan()
      });
    });
    
    svg.addEventListener('panzoomend', function(event) {
      wrapper._panning = false;
      updateCursor();
      
      // Emit our own event
      events.emit('panzoom:end', {
        scale: panzoomInstance.getScale(),
        pan: panzoomInstance.getPan()
      });
    });
    
    // Initial update
    updateCursor();
    updateButtonStates();
  }
  
  /**
   * Set up handlers for zoom control buttons
   * @param {Object} panzoomInstance - The Panzoom instance
   * @param {HTMLElement} wrapper - The wrapper element containing the SVG
   */
  function setupButtonHandlers(panzoomInstance, wrapper) {
    const zoomInBtn = document.getElementById('zoomIn');
    const zoomOutBtn = document.getElementById('zoomOut');
    const zoomResetBtn = document.getElementById('zoomReset');
    
    // Helper to add both click and touch handlers
    function addButtonHandler(button, handler) {
      if (!button) return;
      
      // Click handler
      button.onclick = function(e) {
        handler();
        if (e) e.preventDefault();
        return false;
      };
      
      // Touch handler - respond immediately to touchstart
      button.addEventListener('touchstart', function(e) {
        e.preventDefault(); // Prevent touch delay
        e.stopPropagation(); // Prevent pan-zoom from intercepting
        handler();
      }, { passive: false });
    }
    
    // Add handlers to all buttons
    addButtonHandler(zoomInBtn, () => zoomInFn(panzoomInstance, wrapper));
    addButtonHandler(zoomOutBtn, () => zoomOutFn(panzoomInstance, wrapper));
    addButtonHandler(zoomResetBtn, () => resetFn(panzoomInstance, wrapper));
  }
  
  /**
   * Initialize diagram controls (pan/zoom functionality)
   * Called when diagram needs to be initialized
   */
  function initializeDiagramControls() {
    const diagramWrapper = document.getElementById('diagramWrapper');
    if (!diagramWrapper) {
      console.error('Diagram wrapper not found');
      events.emit('panzoom:error', { error: new Error('Diagram wrapper not found') });
      return;
    }
    
    // Initialize the info button
    const diagramContainer = document.getElementById('diagramContainer');
    if (diagramContainer && window.diagramInfoButton) {
      window.diagramInfoButton.initialize(diagramContainer);
    }
    
    const svg = diagramWrapper.querySelector('svg');
    if (!svg) {
      // SVG not found, set up a mutation observer to watch for it
      setupMutationObserver(diagramWrapper, function(svg) {
        const panzoomInstance = initializePanzoom(svg, diagramWrapper);
        events.emit('panzoom:ready', { instance: panzoomInstance });
      });
      return;
    }
    
    // SVG already exists, initialize panzoom directly
    const panzoomInstance = initializePanzoom(svg, diagramWrapper);
    events.emit('panzoom:ready', { instance: panzoomInstance });
    return panzoomInstance;
  }
  
  /**
   * Set up a mutation observer to watch for SVG being added to the DOM
   * @param {HTMLElement} wrapper - The wrapper element to observe
   * @param {Function} callback - Callback function when SVG is found
   * @returns {MutationObserver} - The created observer
   */
  function setupMutationObserver(wrapper, callback) {
    const observer = new MutationObserver((mutations, obs) => {
      const svg = wrapper.querySelector('svg');
      if (svg) {
        obs.disconnect();
        if (typeof callback === 'function') {
          callback(svg);
        }
      }
    });
    
    observer.observe(wrapper, { 
      childList: true,
      subtree: true
    });
    
    return observer;
  }
  
  // Saved panzoom start values for viewport switching (fullscreen)
  let savedPanzoomStart = null;

  /**
   * Recenter panzoom for the current viewport dimensions.
   * Updates startX/startY so reset() centers correctly, then resets.
   * Saves the original values for later restoration.
   */
  function recenterForViewport() {
    const instance = window.panzoomStore?.instance;
    const wrapper = window.panzoomStore?.wrapper;
    if (!instance || !wrapper) return;

    const svg = wrapper.querySelector('svg');
    if (!svg) return;

    // Cancel any ongoing animation
    if (typeof wrapper.cancelAnimation === 'function') {
      wrapper.cancelAnimation();
    }

    // Save original start values
    const opts = instance.getOptions();
    savedPanzoomStart = { x: opts.startX, y: opts.startY };

    // Calculate centering for the current viewport
    const wrapperRect = wrapper.getBoundingClientRect();
    const svgRect = svg.getBoundingClientRect();
    const currentScale = instance.getScale();
    const naturalWidth = svgRect.width / currentScale;
    const naturalHeight = svgRect.height / currentScale;

    let centerX = 0;
    let centerY = 0;
    if (naturalWidth < wrapperRect.width) {
      centerX = (wrapperRect.width - naturalWidth) / 2;
    }
    if (naturalHeight < wrapperRect.height) {
      centerY = (wrapperRect.height - naturalHeight) / 2;
    }

    // Update start values and reset to the new centered position
    instance.setOptions({ startX: centerX, startY: centerY });
    instance.reset({ animate: false });
  }

  /**
   * Restore panzoom's original startX/startY from before recenterForViewport.
   */
  function restoreOriginalCenter() {
    if (!savedPanzoomStart) return;

    const instance = window.panzoomStore?.instance;
    if (!instance) return;

    instance.setOptions({
      startX: savedPanzoomStart.x,
      startY: savedPanzoomStart.y
    });
    savedPanzoomStart = null;
  }

  /**
   * Clean up resources used by the pan-zoom controller
   */
  function cleanup() {
    events.cleanup();
  }

  // Return the public API
  return {
    initializePanzoom,
    centerNodeInViewport,
    animatedZoomToNode,
    initializeDiagramControls,
    setupMutationObserver,
    recenterForViewport,
    restoreOriginalCenter,
    zoomIn: function() {
      const instance = window.panzoomStore?.instance;
      const wrapper = window.panzoomStore?.wrapper;
      return zoomInFn(instance, wrapper);
    },
    zoomOut: function() {
      const instance = window.panzoomStore?.instance;
      const wrapper = window.panzoomStore?.wrapper;
      return zoomOutFn(instance, wrapper);
    },
    reset: function() {
      const instance = window.panzoomStore?.instance;
      const wrapper = window.panzoomStore?.wrapper;
      return resetFn(instance, wrapper);
    },
    cleanup
  };
}